// The guest's live chat with a restaurant (docs/GUEST_CHAT_2026-10-28.md):
// a launcher on the restaurant's public pages (and the chain29 Contact page,
// where the guest first picks the restaurant). Messages go straight to the
// restaurant's staff on their dining screens / back office; replies arrive
// here (polled every 3 s while open, 20 s while closed). Every message is
// shown as text. States are the server's: online / offline, closed, a booked
// table - nothing is shown as sent until the server says so.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, CheckCircle2, ChevronLeft, Loader2, MessageCircle, Search, Send, X } from 'lucide-react';
import { ChatError, OPEN_EVENT, guestChat, newClientId, saveChat, savedChat } from '@/lib/guestChat';

const OPEN_POLL_MS = 3000;
const CLOSED_POLL_MS = 20000;
const STATUS_POLL_MS = 30000;
const LAST_PLACE = 'c29_chat:last_place';

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function clock(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function readLastPlace() {
  try {
    const raw = localStorage.getItem(LAST_PLACE);
    const p = raw ? JSON.parse(raw) : null;
    return p && p.id ? p : null;
  } catch (e) {
    return null;
  }
}

function writeLastPlace(place) {
  try {
    if (place) localStorage.setItem(LAST_PLACE, JSON.stringify(place));
    else localStorage.removeItem(LAST_PLACE);
  } catch (e) { /* ignore */ }
}

const inputCls = 'w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:border-transparent';

function ReservationFields({ value, onChange, accent }) {
  const set = (k) => (e) => onChange({ ...value, [k]: k === 'party_size' ? Number(e.target.value) : e.target.value });
  return (
    <div className="grid grid-cols-3 gap-2">
      <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
        Guests
        <select value={value.party_size} onChange={set('party_size')} className={inputCls} style={{ '--tw-ring-color': accent }}>
          {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
        Date
        <input type="date" min={today()} value={value.date} onChange={set('date')} className={inputCls} style={{ '--tw-ring-color': accent }} />
      </label>
      <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
        Time
        <input type="time" step="900" value={value.time} onChange={set('time')} className={inputCls} style={{ '--tw-ring-color': accent }} />
      </label>
      <input value={value.note || ''} onChange={set('note')} maxLength={300} placeholder="Anything we should know? (optional)"
        className={`${inputCls} col-span-3`} style={{ '--tw-ring-color': accent }} />
    </div>
  );
}

function Bubble({ m, accent }) {
  if (m.sender === 'system') {
    if (m.kind === 'reservation_created') {
      const r = m.meta || {};
      return (
        <div className="mx-auto w-full max-w-[92%] rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-900">
          <div className="flex items-center gap-2 text-sm font-bold"><CheckCircle2 className="h-4 w-4" />Table booked</div>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm">{m.body}</p>
          {r.confirmation_code && !String(m.body || '').includes(r.confirmation_code) && <p className="mt-1 text-xs font-semibold tracking-wide">Confirmation {r.confirmation_code}</p>}
        </div>
      );
    }
    return <p className="mx-auto max-w-[90%] rounded-full bg-slate-100 px-3 py-1.5 text-center text-xs font-medium text-slate-500">{m.body}</p>;
  }
  const mine = m.sender === 'guest';
  return (
    <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[15px] leading-snug shadow-sm ${mine ? 'rounded-br-md text-white' : 'rounded-bl-md bg-white text-slate-900 ring-1 ring-slate-200'}`}
        style={mine ? { background: accent } : undefined}>
        {m.kind === 'reservation_request' && (
          <span className={`mb-1 flex items-center gap-1.5 text-xs font-bold ${mine ? 'text-white/90' : 'text-slate-500'}`}><CalendarDays className="h-3.5 w-3.5" />Table request</span>
        )}
        <span className="whitespace-pre-wrap break-words">{m.body}</span>
      </div>
      <span className="mt-1 px-1 text-[11px] text-slate-400">{!mine && m.author ? `${m.author} · ` : ''}{clock(m.created_at)}{m.pending ? ' · sending…' : ''}</span>
    </div>
  );
}

function PlacePicker({ onPick, accent }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); setError(null); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const data = await guestChat.findRestaurants(q.trim());
        if (alive) { setFound(data.restaurants || []); setError(null); }
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setBusy(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
      <p className="text-sm leading-relaxed text-slate-600">Chat goes straight to the restaurant&apos;s staff on their POS - ask a question or book a table. Which restaurant?</p>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Restaurant name" maxLength={80}
          className={`${inputCls} pl-9`} style={{ '--tw-ring-color': accent }} aria-label="Search restaurants" />
      </div>
      {busy && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Searching…</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {!busy && q.trim().length >= 2 && !found.length && !error && <p className="text-sm text-slate-500">No restaurant by that name yet.</p>}
      <ul className="flex flex-col gap-2">
        {found.map((r) => (
          <li key={r.id}>
            <button type="button" onClick={() => onPick({ id: r.id, name: r.name })}
              className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-slate-300 hover:shadow-sm">
              <span className="block text-[15px] font-semibold text-slate-900">{r.name}</span>
              {r.address && <span className="block truncate text-xs text-slate-500">{r.address}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function GuestChat({ restaurantId, restaurantName, accent = '#F97316', sourcePage = 'restaurant', label = 'Chat with us', side = 'right' }) {
  const fixed = !!restaurantId;
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(() => (fixed ? { id: restaurantId, name: restaurantName } : readLastPlace()));
  const [saved, setSaved] = useState(() => savedChat(fixed ? restaurantId : readLastPlace()?.id));
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', sms_opt_in: false });
  const [askTable, setAskTable] = useState(false);
  const [table, setTable] = useState({ party_size: 2, date: today(), time: '19:00', note: '' });
  const listRef = useRef(null);
  const lastId = useRef(0);

  // the restaurant name on a fixed page may arrive after the first render
  useEffect(() => { if (fixed) setPlace({ id: restaurantId, name: restaurantName }); }, [fixed, restaurantId, restaurantName]);

  const reset = useCallback(() => {
    lastId.current = 0;
    setConv(null);
    setMessages([]);
    setError(null);
  }, []);

  const pick = (p) => {
    writeLastPlace(p);
    setPlace(p);
    reset();
    setSaved(savedChat(p.id));
  };

  // another part of the page asks to open the chat (e.g. "Find a table")
  useEffect(() => {
    const onOpen = (e) => {
      setOpen(true);
      const r = e.detail?.reservation;
      if (r) {
        setAskTable(true);
        setTable((t) => ({ ...t, ...Object.fromEntries(Object.entries(r).filter(([, v]) => v !== undefined && v !== null && v !== '')) }));
      }
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const merge = useCallback((incoming) => {
    if (!incoming?.length) return;
    setMessages((list) => {
      const known = new Set(list.filter((m) => !m.pending).map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      if (!fresh.length) return list;
      return [...list.filter((m) => !m.pending), ...fresh].sort((a, b) => a.id - b.id);
    });
    lastId.current = Math.max(lastId.current, ...incoming.map((m) => m.id));
  }, []);

  // replies arrive by polling the guest's own chat (token only)
  const token = saved?.token;
  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const data = await guestChat.read(token, lastId.current);
      setConv(data.conversation);
      setOnline(data.conversation?.online ?? null);
      merge(data.messages);
      setError(null);
    } catch (e) {
      if (e instanceof ChatError && (e.status === 404 || e.status === 401)) {
        saveChat(place?.id, null); // the chat expired (7 days idle) or was removed
        setSaved(null);
        reset();
      }
    }
  }, [token, merge, place?.id, reset]);

  useEffect(() => {
    if (!token) return undefined;
    poll();
    const t = setInterval(poll, open ? OPEN_POLL_MS : CLOSED_POLL_MS);
    return () => clearInterval(t);
  }, [token, open, poll]);

  // before a chat exists: is anyone there to answer?
  useEffect(() => {
    if (!open || token || !place?.id) return undefined;
    let alive = true;
    const load = () => guestChat.status(place.id).then((s) => { if (alive) setOnline(!!s.online); }).catch(() => { if (alive) setOnline(null); });
    load();
    const t = setInterval(load, STATUS_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [open, token, place?.id]);

  // what the guest has seen (for the launcher's dot)
  const newestStaff = useMemo(() => messages.filter((m) => m.sender !== 'guest').reduce((n, m) => Math.max(n, m.id), 0), [messages]);
  useEffect(() => {
    if (open && token && newestStaff > (saved?.seen || 0)) {
      const next = { ...saved, seen: newestStaff };
      saveChat(place?.id, next);
      setSaved(next);
    }
  }, [open, token, newestStaff, saved, place?.id]);
  const unread = !open && token && newestStaff > (saved?.seen || 0);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length, open]);

  const tableBody = () => (askTable ? { party_size: table.party_size, date: table.date, time: table.time, note: table.note || null } : null);

  const start = async (e) => {
    e.preventDefault();
    if (!place?.id) return;
    setBusy(true);
    setError(null);
    try {
      const data = await guestChat.start({
        restaurant_id: place.id, name: form.name, phone: form.phone || null, email: form.email || null,
        sms_opt_in: !!(form.sms_opt_in && form.phone), message: draft || null, reservation: tableBody(),
        source_page: sourcePage,
      });
      const next = { token: data.token, seen: 0 };
      saveChat(place.id, next);
      setSaved(next);
      setConv(data.conversation);
      setOnline(data.conversation?.online ?? null);
      merge(data.messages);
      setDraft('');
      setAskTable(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    const reservation = tableBody();
    if ((!text && !reservation) || !token) return;
    const clientId = newClientId();
    const optimistic = text ? { id: Number.MAX_SAFE_INTEGER, sender: 'guest', kind: 'text', body: text, pending: true, created_at: new Date().toISOString() } : null;
    if (optimistic) setMessages((list) => [...list, optimistic]);
    setBusy(true);
    setError(null);
    try {
      const data = await guestChat.send(token, { body: text || null, reservation, client_id: clientId });
      merge(data.messages);
      setDraft('');
      setAskTable(false);
      setConv((c) => (c ? { ...c, status: 'open' } : c));
    } catch (err) {
      setMessages((list) => list.filter((m) => !m.pending));
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const forget = () => {
    saveChat(place?.id, null);
    setSaved(null);
    reset();
  };

  const texts = conv ? conv.texts_on : !!(form.sms_opt_in && form.phone);
  const statusLine = online === null ? null
    : online ? 'Online now - staff usually reply within minutes'
      : texts ? "Restaurant is offline - we'll text you when they reply"
        : 'Restaurant is offline - leave a message and the reply will appear here';

  return (
    <>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} aria-label={label}
          className={`fixed bottom-5 ${side === 'left' ? 'left-5' : 'right-5'} z-[60] flex h-14 items-center gap-2 rounded-full px-5 text-[15px] font-semibold text-white shadow-[0_12px_30px_rgba(15,23,42,.28)] transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60`}
          style={{ background: accent }} data-testid="guest-chat-launcher">
          <MessageCircle className="h-5 w-5" />
          <span className="hidden sm:inline">{label}</span>
          {unread && <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-red-500" aria-label="New reply" />}
        </button>
      )}
      {open && (
        <section role="dialog" aria-label={`Chat with ${place?.name || 'a restaurant'}`} data-testid="guest-chat-panel"
          className={`fixed bottom-3 ${side === 'left' ? 'left-3 sm:left-5' : 'right-3 sm:right-5'} z-[60] flex h-[min(620px,calc(100vh-24px))] w-[min(390px,calc(100vw-24px))] flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-[0_24px_70px_rgba(15,23,42,.35)] ring-1 ring-black/5 sm:bottom-5`}>
          <header className="flex flex-none items-center gap-3 px-4 py-3.5 text-white" style={{ background: accent }}>
            {!fixed && place && !token && (
              <button type="button" onClick={() => { writeLastPlace(null); setPlace(null); reset(); setSaved(null); }} aria-label="Pick another restaurant"
                className="-ml-1 flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/15"><ChevronLeft className="h-5 w-5" /></button>
            )}
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[16px] font-bold">{place?.name || 'Chat with a restaurant'}</span>
              {place && statusLine && (
                <span className="flex items-center gap-1.5 text-xs text-white/90">
                  <span className={`h-2 w-2 rounded-full ${online ? 'bg-emerald-300' : 'bg-white/50'}`} />
                  {statusLine}
                </span>
              )}
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/15">
              <X className="h-5 w-5" />
            </button>
          </header>

          {!place && <PlacePicker onPick={pick} accent={accent} />}

          {place && !token && (
            <form onSubmit={start} className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
              <p className="text-sm leading-relaxed text-slate-600">Write to the team at {place.name}. Your message goes straight to their POS.</p>
              <input required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name" aria-label="Your name" className={inputCls} style={{ '--tw-ring-color': accent }} />
              <div className="grid grid-cols-2 gap-2">
                <input type="tel" maxLength={30} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone (optional)" aria-label="Phone (optional)" className={inputCls} style={{ '--tw-ring-color': accent }} />
                <input type="email" maxLength={254} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="E-mail (optional)" aria-label="E-mail (optional)" className={inputCls} style={{ '--tw-ring-color': accent }} />
              </div>
              <label className={`flex items-start gap-2 text-xs leading-snug ${form.phone ? 'text-slate-600' : 'text-slate-400'}`}>
                <input type="checkbox" disabled={!form.phone} checked={!!(form.sms_opt_in && form.phone)}
                  onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked })} className="mt-0.5" />
                Text me the restaurant&apos;s replies when I&apos;m not on this page (message rates may apply).
              </label>
              <button type="button" onClick={() => setAskTable((v) => !v)} aria-pressed={askTable}
                className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${askTable ? 'border-transparent text-white' : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300'}`}
                style={askTable ? { background: accent } : undefined}>
                <CalendarDays className="h-4 w-4" />I&apos;d like to reserve a table
              </button>
              {askTable && <ReservationFields value={table} onChange={setTable} accent={accent} />}
              <textarea rows={3} maxLength={2000} value={draft} onChange={(e) => setDraft(e.target.value)}
                placeholder={askTable ? 'Add a message (optional)' : 'Your message'} aria-label="Your message"
                className={`${inputCls} resize-none`} style={{ '--tw-ring-color': accent }} required={!askTable} />
              {error && <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
              <button type="submit" disabled={busy}
                className="flex h-12 items-center justify-center gap-2 rounded-xl text-[15px] font-bold text-white disabled:opacity-60" style={{ background: accent }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {askTable ? 'Send table request' : 'Start chat'}
              </button>
              <p className="text-center text-[11px] leading-snug text-slate-400">The restaurant sees your name and the contact details you give. Chats are deleted after 7 days without a message.</p>
            </form>
          )}

          {place && token && (
            <>
              <div ref={listRef} className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4" aria-live="polite">
                {!messages.length && <p className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading your chat…</p>}
                {messages.map((m) => <Bubble key={`${m.id}-${m.pending ? 'p' : ''}`} m={m} accent={accent} />)}
                {conv?.status === 'open' && messages.length > 0 && messages[messages.length - 1].sender === 'guest' && online === false && (
                  <p className="mx-auto max-w-[90%] text-center text-xs text-slate-500">
                    {conv.texts_on ? "Nobody is at the POS right now - we'll text you when they reply." : 'Nobody is at the POS right now - the reply will appear here.'}
                  </p>
                )}
              </div>
              <form onSubmit={send} className="flex flex-none flex-col gap-2 border-t border-slate-200 bg-white p-3">
                {askTable && <ReservationFields value={table} onChange={setTable} accent={accent} />}
                {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700">{error}</p>}
                <div className="flex items-end gap-2">
                  <button type="button" onClick={() => setAskTable((v) => !v)} aria-pressed={askTable} aria-label="I'd like to reserve a table" title="I'd like to reserve a table"
                    className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl border transition ${askTable ? 'border-transparent text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    style={askTable ? { background: accent } : undefined}>
                    <CalendarDays className="h-5 w-5" />
                  </button>
                  <textarea rows={1} maxLength={2000} value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
                    placeholder={askTable ? 'Add a note (optional)' : 'Write a message'} aria-label="Write a message"
                    className={`${inputCls} max-h-28 min-h-[44px] resize-none py-2.5`} style={{ '--tw-ring-color': accent }} />
                  <button type="submit" disabled={busy || (!draft.trim() && !askTable)} aria-label="Send"
                    className="flex h-11 w-11 flex-none items-center justify-center rounded-xl text-white disabled:opacity-50" style={{ background: accent }}>
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </button>
                </div>
                <button type="button" onClick={forget} className="self-center text-[11px] text-slate-400 underline-offset-2 hover:underline">Start a new chat</button>
              </form>
            </>
          )}
        </section>
      )}
    </>
  );
}
